import React, { useEffect, useState, useContext } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'

import { toast } from 'react-toastify'
import { buildApiUrl } from '../utils/utils'
import { getPostAuthRedirectPath } from '../utils/routing'
import {
  getTokenLocalStorage,
  putTokenLocalStorage,
  removeTokenLocalStorage,
  removeInviteHashLocalStorage
} from '../utils/localStorage'
import { UserContext } from './UserContext'

interface AuthContextType {
  sessionId: string | null
  bearerToken: string | null
  handleLogin: (callback: VoidFunction) => void
  handleLogout: (callback: VoidFunction) => void
  isLoadingAuth: boolean
  evaluateToken: () => void
}

export const AuthContext = React.createContext<AuthContextType>(null!)

export const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [bearerToken, setBearerToken] = useState<string | null>(() => {
    // Use lazy initializer to safely access localStorage only in browser
    return getTokenLocalStorage()
  })
  const [isLoadingAuth, setIsLoadingAuth] = useState<boolean>(false)
  const { setUser, setMerchState } = useContext(UserContext)
  const { user } = useContext(UserContext)
  const [searchParams] = useSearchParams()

  const navigate = useNavigate()

  const handleLogin = (callback: VoidFunction) => {
    setIsLoadingAuth(true)
    axios
      .post(buildApiUrl('sessions'), {
        successUrl: process.env.REACT_APP_LOGIN_REDIRECT_URL
      })
      // .post(`https:/localhost:8080/api/sessions?key=${process.env.REACT_APP_API_KEY}`, {
      //   successUrl: process.env.REACT_APP_LOGIN_REDIRECT_URL
      // })
      .then(response => {
        console.log('✅ Login success:', response.data)
        window.location.replace(response.data.Location)
      })
      .catch(error => {
        console.error('❌ Login error:', error)
        console.error('❌ Error response:', error.response?.data)
        console.error('❌ Error status:', error.response?.status)
        setIsLoadingAuth(false)
        toast.error(`Login failed: ${error.response?.data?.message || error.message}`)
      })
    callback()
  }

  const handleLogout = (callback: VoidFunction) => {
    removeTokenLocalStorage()
    setBearerToken(null)
    removeInviteHashLocalStorage()
    callback()
  }

  const handleFetchBearerToken = async (sessionId: string) => {
    try {
      setIsLoadingAuth(true)
      const response = await axios.get(buildApiUrl('sessions', sessionId))
      console.log('sessionsResponse: ', response)
      setBearerToken(response.data.token)
      putTokenLocalStorage(response.data.token)
      setUser(response.data.personData)
      setMerchState(response.data.personData.memberProducts)

      // Centralized redirect decision
      navigate(getPostAuthRedirectPath(response.data.personData))

      setIsLoadingAuth(false)
    } catch (error) {
      console.log(error)
      setIsLoadingAuth(false)
    }
  }

  const evaluateToken = async () => {
    try {
      if (bearerToken) {
        const response = await axios.get(buildApiUrl('sessions/evaluateToken'), {
          headers: { 'x-tietoni-token': `Bearer ${bearerToken}` }
        })
        // Restore user data if user is null (page refresh) or if it matches current user
        if (!user || user.els_henkilonumero == response.data.personData.els_henkilonumero) {
          setUser(response.data.personData)
          setMerchState(response.data.personData.memberProducts)
        }
      }
    } catch (error) {
      // Maybe notify user that login has expired and needs to login again?
      // react-tostify, material-ui snackbar etc...
      console.log('Bearer token not valid, removing from storage')
      removeTokenLocalStorage()
      setBearerToken(null)
      console.log(error)
      removeInviteHashLocalStorage()
    }
  }

  useEffect(() => {
    evaluateToken()
  }, [])

  useEffect(() => {
    setSessionId(searchParams.get('sessionId'))
  }, [])

  useEffect(() => {
    if (sessionId) {
      handleFetchBearerToken(sessionId)
    }
  }, [sessionId])

  const value = { sessionId, bearerToken, handleLogin, handleLogout, isLoadingAuth, evaluateToken }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthProvider
